import type {
  BabyProfile,
  CopilotContext,
  DevelopmentalLeapResult,
  DevelopmentalNorm,
  LogEvent,
} from "../types/domain";
import { getNormForWeek } from "../../data/norms";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export function buildLogsJson(logs: LogEvent[], cutoffMs: number): string {
  const cutoff = Date.now() - cutoffMs;
  const filtered = logs
    .filter((l) => new Date(l.occurredAt).getTime() >= cutoff)
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
    .map((l) => {
      const entry: Record<string, unknown> = {
        type: l.type,
        occurredAt: l.occurredAt,
        payload: l.payload,
      };
      if (l.notes) entry.notes = l.notes;
      return entry;
    });
  return JSON.stringify(filtered);
}

export function buildCopilotContext(
  baby: BabyProfile,
  ageWeeks: number,
  logs: LogEvent[],
  norm: DevelopmentalNorm | null,
  completedMilestones: string[]
): CopilotContext {
  const earliest = logs.reduce<string | null>(
    (min, l) => (!min || l.occurredAt < min ? l.occurredAt : min),
    null
  );

  return {
    currentTimeISO: new Date().toISOString(),
    ageWeeks,
    sex: baby.sex ?? null,
    loggingSinceISO: earliest ?? new Date().toISOString(),
    logs7days: buildLogsJson(logs, SEVEN_DAYS_MS),
    completedMilestones,
    normSummary: norm
      ? `At ${ageWeeks} weeks, targets: ${norm.feedingTargetMl}ml/day (min ${norm.feedingMinMl}ml), ${norm.diaperMin}+ wet diapers, ${norm.growthMinGPerWeek}–${norm.growthMaxGPerWeek}g/week.`
      : undefined,
  };
}

const LANDMARK_WEEKS = [0, 4, 6, 8, 12, 16, 20, 24, 32, 36, 40, 52, 64, 78, 92, 104];

export function detectDevelopmentalLeap(
  ageWeeks: number,
  norm: DevelopmentalNorm | null
): DevelopmentalLeapResult {
  const nextLandmarkWeek = LANDMARK_WEEKS.find((w) => w > ageWeeks) ?? null;

  if (nextLandmarkWeek === null) {
    return {
      approachingLeap: false,
      daysUntilNextLandmark: null,
      nextLandmarkWeek: null,
      headsUpPreview: null,
    };
  }

  const daysUntilNextLandmark = Math.round((nextLandmarkWeek - ageWeeks) * 7);
  const approachingLeap = daysUntilNextLandmark <= 14;
  const nextNorm = getNormForWeek(nextLandmarkWeek);
  const eventName = nextNorm?.eventName;
  const headsUpPreview = nextNorm?.headsUp.split(".")[0] ?? norm?.headsUp.split(".")[0] ?? null;

  return {
    approachingLeap,
    daysUntilNextLandmark,
    nextLandmarkWeek,
    headsUpPreview,
    eventName,
    summary: approachingLeap
      ? `Approaching week ${nextLandmarkWeek} developmental leap in ~${daysUntilNextLandmark} days.${headsUpPreview ? ` ${headsUpPreview}.` : ""}`
      : undefined,
  };
}
